import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  NgZone,
  OnInit,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ApiClientConstant } from '@cureskin/api-client';
import { ChatService } from '@services/chat-service/chat-service';
import { EventLoggerService } from '@services/event-logger-service';
import { ExperimentService } from '@services/experiment/experiment.service';
import { FollowUpChatService } from '@services/followup-chat-service/followup-chat.service';
import { LocalStorageService } from '@services/local-storage-service';
import { SentryErrorHandler } from '@services/sentry-logger';
import { LinearLoaderBarComponent } from '@shared/linear-loader-bar/linear-loader-bar.component';
import { ReportGettingReadyComponent } from '../../user/user-view/user-home/level-two-banners/report-getting-ready/report-getting-ready.component';
import { AppConfig } from '../../app.config';
import { stepperFillAnimation } from './follow-up-questions-widget.animations';
import { getFollowUpQuestionTopic } from './follow-up-question-topics';
import {
  ACTIVE_FOLLOWUP_CACHE_KEY,
  CLICK_FEEDBACK_LOCK_MS,
  COMPLETED_FOLLOWUP_CACHE_KEY,
  DEFAULT_SKIP_QUESTIONS,
  FollowUpInput,
  INLINE_COMPLETION_DISMISS_MS,
  WidgetPlacement,
} from './follow-up-questions-widget.types';

@Component({
  selector: 'app-follow-up-questions-widget',
  templateUrl: './follow-up-questions-widget.component.html',
  imports: [CommonModule, ReportGettingReadyComponent, LinearLoaderBarComponent],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [stepperFillAnimation],
  providers: [ChatService, FollowUpChatService],
})
export class FollowUpQuestionsWidgetComponent implements OnInit {
  readonly followUp = input.required<Table.FollowUp>();
  readonly user = input.required<Table.User>();
  readonly userImageUrl = input<string | undefined>(undefined);
  readonly placement = input<WidgetPlacement>(WidgetPlacement.Inline);

  readonly completed = output<void>();
  readonly fallbackToChat = output<void>();

  private readonly followUpChatService = inject(FollowUpChatService);
  private readonly eventLogger = inject(EventLoggerService);
  private readonly experimentService = inject(ExperimentService);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly sentry = inject(SentryErrorHandler);
  private readonly zone = inject(NgZone);
  private readonly appConfig = inject(AppConfig);
  private readonly destroyRef = inject(DestroyRef);

  readonly loading = signal<boolean>(true);
  readonly isCompleted = signal<boolean>(false);
  readonly markingReady = signal<boolean>(false);
  readonly botReplyInProcess = signal<boolean>(false);
  readonly userResponseSaveInProcess = signal<boolean>(false);
  readonly errorMessage = signal<string>('');
  readonly waitingForFirstQuestion = signal<boolean>(false);

  readonly totalSlots = computed<number>((): number => this.computeTotalSlots(this.user()));
  readonly filledSlots = computed<number>(
    (): number => Math.min(this.questionNumber(), this.totalSlots()),
  );
  readonly topicLabel = signal<string>('');
  readonly questionNumber = signal<number>(1);
  readonly questionText = signal<string>('');
  readonly options = signal<FollowUpInput[]>([]);
  readonly selectedOptionIndex = signal<number | null>(null);

  readonly slotArray = computed<number[]>((): number[] => new Array(this.totalSlots()).fill(0));
  readonly inputsDisabled = computed<boolean>(
    (): boolean => this.userResponseSaveInProcess() || this.botReplyInProcess(),
  );
  readonly showLoader = computed<boolean>(
    (): boolean => (this.loading() || this.waitingForFirstQuestion())
      && !this.isCompleted() && !this.errorMessage(),
  );
  readonly showCompletionBanner = computed<boolean>((): boolean => this.placement() === WidgetPlacement.Sheet);
  readonly showCompletionCta = computed<boolean>((): boolean => this.placement() === WidgetPlacement.Sheet);
  readonly cornerClass = computed<string>(
    (): string => this.placement() === WidgetPlacement.Sheet ? 'tw-rounded-t-3xl' : 'tw-rounded-3xl',
  );

  private latestBotMessage: Table.FollowUpChat | undefined;
  private skipQuestionIds: readonly string[] = DEFAULT_SKIP_QUESTIONS;
  private readonly autoAnswered = new Set<string>();
  private inlineCompletionTimer: ReturnType<typeof setTimeout> | undefined;
  private clickFeedbackLockUntilMs: number = 0;
  private clickFeedbackLockTimer: ReturnType<typeof setTimeout> | undefined;
  private bufferedMessages: Table.FollowUpChat[] | null = null;

  constructor() {
    this.destroyRef.onDestroy((): void => {
      this.followUpChatService.stopBotReplyTimer();
      if (this.inlineCompletionTimer) clearTimeout(this.inlineCompletionTimer);
      if (this.clickFeedbackLockTimer) clearTimeout(this.clickFeedbackLockTimer);
    });
  }

  ngOnInit(): void {
    void this.initialise();
  }

  private async initialise(): Promise<void> {
    if (this.placement() === WidgetPlacement.Sheet) {
      this.eventLogger.cleverTapEvent('impression', JSON.stringify({
        name: 'follow_up_questions_bs',
      }));
    }

    await this.experimentService.experimentsLoaded;
    const exp = this.experimentService.getFupQuestionsNewUiExperiment();
    const variantSkip = exp?.variant?.skipQuestion;
    this.skipQuestionIds = variantSkip?.length ? variantSkip : DEFAULT_SKIP_QUESTIONS;

    this.followUpChatService.configureForFollowUp(this.user(), this.followUp());

    const followUpId = this.followUp()?.id;
    if (followUpId) {
      this.localStorageService.setValue(ACTIVE_FOLLOWUP_CACHE_KEY, followUpId);
    }

    this.followUpChatService.allChats$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((messages): void => this.handleMessages(messages as unknown as Table.FollowUpChat[]));

    this.followUpChatService.showThinking$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((thinking: boolean): void => {
        this.zone.run((): void => this.botReplyInProcess.set(thinking));
      });

    try {
      await this.followUpChatService.loadChatHistory();
    } catch (err) {
      this.handleError(err);
    } finally {
      this.zone.run((): void => this.loading.set(false));
    }
  }

  acknowledgeCompletion(): void {
    this.emitCompleted();
  }

  async selectOption(option: FollowUpInput, index: number): Promise<void> {
    if (!this.latestBotMessage || this.userResponseSaveInProcess() || this.botReplyInProcess()) return;
    if (!option) return;

    if (option.action !== 'TREE') {
      this.emitFallback();
      return;
    }

    this.selectedOptionIndex.set(index);
    this.startClickFeedbackLock();

    const message = option.type === 'TEXT' ? option.value || option.text : option.text;
    if (!message) return;

    const previousMessage = this.latestBotMessage;
    this.userResponseSaveInProcess.set(true);
    this.errorMessage.set('');

    const prefix = this.placement() === WidgetPlacement.Sheet ? 'follow_up_questions_bs' : 'follow_up_questions_banner';
    this.eventLogger.cleverTapEvent('click', JSON.stringify({
      name: `${prefix}_ans_q${this.questionNumber()}`,
    }));

    try {
      await this.followUpChatService.sendMessage(previousMessage, message);
    } catch (err) {
      this.followUpChatService.setThinking(false);
      if (this.isPendingAnswersError(err)) {
        await this.followUpChatService.loadChatHistory();
      } else {
        this.handleError(err);
      }
    } finally {
      this.userResponseSaveInProcess.set(false);
    }
  }

  private isPendingAnswersError(err: unknown): boolean {
    if (typeof err !== 'object' || err === null) return false;
    const candidate = err as { code?: unknown; message?: unknown };
    return candidate.code === 412
      && typeof candidate.message === 'string'
      && candidate.message.includes('Please answer pending questions.');
  }

  private computeTotalSlots(user: Table.User | undefined): number {
    const female = ApiClientConstant.User?.Gender?.FEMALE || 'Female';
    return user?.get?.('Gender') === female ? 4 : 3;
  }

  richText(raw: string | null | undefined): string {
    return (raw ?? '').replaceAll('\n', '<br>');
  }

  private startClickFeedbackLock(): void {
    this.clickFeedbackLockUntilMs = Date.now() + CLICK_FEEDBACK_LOCK_MS;
    if (this.clickFeedbackLockTimer) clearTimeout(this.clickFeedbackLockTimer);
    this.clickFeedbackLockTimer = setTimeout(
      (): void => this.zone.run((): void => this.releaseClickFeedbackLock()),
      CLICK_FEEDBACK_LOCK_MS,
    );
  }

  private releaseClickFeedbackLock(): void {
    this.clickFeedbackLockUntilMs = 0;
    this.clickFeedbackLockTimer = undefined;
    const pending = this.bufferedMessages;
    this.bufferedMessages = null;
    if (pending) this.applyMessages(pending);
  }

  private handleMessages(messages: Table.FollowUpChat[]): void {
    if (Date.now() < this.clickFeedbackLockUntilMs) {
      this.bufferedMessages = messages;
      return;
    }
    this.applyMessages(messages);
  }

  private applyMessages(messages: Table.FollowUpChat[]): void {
    this.zone.run((): void => {
      if (!messages.length) {
        this.latestBotMessage = undefined;
        this.questionText.set('');
        this.options.set([]);
        return;
      }

      const latest = this.followUpChatService.getLatestActionableMessage(messages);
      if (this.followUpChatService.isCompletionMessage(latest)) {
        this.markCompleted();
        return;
      }

      if (this.isSkippableQuestion(latest)) {
        if (this.canTriggerAutoAnswer(latest)) {
          this.triggerAutoAnswerSkippable(latest);
        }
        if (this.loading()) {
          this.waitingForFirstQuestion.set(true);
        }
        this.latestBotMessage = undefined;
        this.questionText.set('');
        this.options.set([]);
        return;
      }

      const ownerBot = this.appConfig.Shared.Assistant.Owner.BOT;
      const isLatestActiveBotQuestion = latest?.get?.('Owner') === ownerBot
        && Array.isArray(latest.get('Inputs'))
        && latest.get('Inputs').length > 0;

      const newBotMessage = isLatestActiveBotQuestion ? latest : undefined;
      if (newBotMessage !== this.latestBotMessage) {
        this.selectedOptionIndex.set(null);
      }
      this.latestBotMessage = newBotMessage;
      if (this.latestBotMessage && this.waitingForFirstQuestion()) {
        this.waitingForFirstQuestion.set(false);
      }

      if (this.latestBotMessage) {
        this.questionText.set(this.latestBotMessage.get('Message') || '');
        const rawInputs = (this.latestBotMessage.get('Inputs') || []) as FollowUpInput[];
        const inputs: FollowUpInput[] = rawInputs
          .filter((input): boolean => input?.type !== 'TEXT' || !!input?.text);
        this.options.set(inputs);

        const ordinal = this.computeQuestionOrdinal(messages, this.latestBotMessage);
        this.questionNumber.set(ordinal);
        this.topicLabel.set(getFollowUpQuestionTopic(this.latestBotMessage.get('questionId'), ordinal));
      } else {
        this.questionText.set('');
        this.options.set([]);
      }
    });
  }

  private isSkippableQuestion(latest: Table.FollowUpChat | undefined): boolean {
    if (!latest) return false;
    const questionId = latest.get?.('questionId');
    if (!questionId || !this.skipQuestionIds.includes(questionId)) return false;
    const ownerBot = this.appConfig.Shared.Assistant.Owner.BOT;
    if (latest.get('Owner') !== ownerBot) return false;
    const inputs = (latest.get('Inputs') || []) as FollowUpInput[];
    if (!inputs.length) return false;
    return inputs.some((input): boolean => input?.action === 'TREE' && input?.type === 'BUTTON');
  }

  private canTriggerAutoAnswer(latest: Table.FollowUpChat | undefined): boolean {
    if (!latest?.id) return false;
    if (this.autoAnswered.has(latest.id)) return false;
    if (this.userResponseSaveInProcess()) return false;
    return true;
  }

  private async triggerAutoAnswerSkippable(latest: Table.FollowUpChat): Promise<void> {
    this.autoAnswered.add(latest.id);
    const inputs = (latest.get('Inputs') || []) as FollowUpInput[];
    const treeButton = inputs.find((input): boolean => input?.action === 'TREE' && input?.type === 'BUTTON');
    const messageText: string = treeButton?.text || treeButton?.value || '';
    if (!messageText) {
      this.autoAnswered.delete(latest.id);
      return;
    }
    try {
      await this.followUpChatService.sendMessage(latest, messageText);
    } catch (err) {
      this.autoAnswered.delete(latest.id);
      this.handleError(err);
    }
  }

  private computeQuestionOrdinal(messages: Table.FollowUpChat[], currentMessage: Table.FollowUpChat): number {
    const ownerBot = this.appConfig.Shared.Assistant.Owner.BOT;
    let ordinal = 0;
    for (const message of messages) {
      if (message?.get?.('Owner') !== ownerBot) continue;
      if (message?.get?.('Mode') === 'SMS_COPY') continue;
      if (this.skipQuestionIds.includes(message?.get?.('questionId'))) continue;
      const inputs = message.get('Inputs');
      if (!Array.isArray(inputs) || inputs.length === 0) continue;
      ordinal += 1;
      if (message === currentMessage) break;
    }
    return Math.max(ordinal, 1);
  }

  private markCompleted(): void {
    if (this.isCompleted() || this.markingReady()) return;
    this.markingReady.set(true);
    this.botReplyInProcess.set(false);
    this.followUpChatService.stopBotReplyTimer();
    this.localStorageService.delete(ACTIVE_FOLLOWUP_CACHE_KEY);
    const followUpId = this.followUp()?.id;
    if (followUpId) {
      this.localStorageService.setValue(COMPLETED_FOLLOWUP_CACHE_KEY, followUpId);
    }
    this.callMarkReadyAndShowBanner();
  }

  private callMarkReadyAndShowBanner(): void {
    this.zone.run((): void => {
      this.markingReady.set(false);
      this.isCompleted.set(true);
    });

    if (this.placement() === WidgetPlacement.Inline) {
      this.inlineCompletionTimer = setTimeout((): void => {
        this.zone.run((): void => this.emitCompleted());
      }, INLINE_COMPLETION_DISMISS_MS);
    }
  }

  private emitCompleted(): void {
    if (this.inlineCompletionTimer) {
      clearTimeout(this.inlineCompletionTimer);
      this.inlineCompletionTimer = undefined;
    }
    this.completed.emit();
  }

  private emitFallback(): void {
    this.fallbackToChat.emit();
  }

  private handleError(err: unknown): void {
    if (err instanceof Error) {
      this.sentry.handleError(err);
    }
    const message = this.formatErrorMessage(err);
    this.zone.run((): void => {
      this.errorMessage.set(message);
      this.botReplyInProcess.set(false);
    });
  }

  private formatErrorMessage(err: unknown): string {
    if (err instanceof Error) return err.message;
    if (typeof err === 'string') return err;
    return 'Something went wrong';
  }
}
